// Risk Driver "Why" Explanations — per-parameter severity explanations
// Used in project detail modals and risk matrix drill-downs to explain
// why a specific risk parameter scored at its current level.

export interface RiskDriverExplanationVariant {
  whyExplanation: string
  correctiveAction: string
  estimatedImpact: string
}

export interface RiskDriverExplanationData {
  paramId: string
  paramLabel: string
  severity: 'critical' | 'high' | 'medium'
  variants: RiskDriverExplanationVariant[]
}

export interface RiskDriverExplanation {
  paramId: string
  paramLabel: string
  severity: 'critical' | 'high' | 'medium'
  whyExplanation: string
  correctiveAction: string
  estimatedImpact: string
}

export const RISK_DRIVER_EXPLANATIONS_DATA: RiskDriverExplanationData[] = [
  {
    paramId: 'ptpQuality',
    paramLabel: 'PTP Quality',
    severity: 'critical',
    variants: [
      {
        whyExplanation:
          'Only 4 of 12 PTPs submitted this week included hazard-specific controls, red zones, or rescue steps. Site performance is 33% versus a portfolio benchmark of 85%, with the weakest plans tied to lifting, edge work, and energized tie-ins.',
        correctiveAction:
          'Require superintendent sign-off on every high-risk PTP through Friday, run a 30-minute refresher for all foremen on writing task-step hazards and controls, and audit the next 10 PTPs before crews start work.',
        estimatedImpact:
          'Should improve hazard recognition before work starts and reduce repeat findings on the same high-risk tasks.',
      },
      {
        whyExplanation:
          'Recent audits show 60% of PTPs for the steel erection phase are copy-pasted from previous days without updating weather conditions or new adjacent trades. Critical lift plans were missing from 2 of 3 crane setups.',
        correctiveAction:
          'Implement a hard stop on generic PTPs. Foremen must physically walk the work area before writing the PTP, and safety must initial all critical lift plans daily.',
        estimatedImpact:
          'Ensures daily hazards are actually evaluated rather than treated as a paperwork exercise, directly reducing struck-by risks.',
      },
      {
        whyExplanation:
          'PTPs for the MEP rough-in crews lack specific lockout/tagout (LOTO) details. 5 of 8 reviewed PTPs simply stated "be careful" instead of listing the specific breakers and verification steps required by 29 CFR 1926.417.',
        correctiveAction:
          'Mandate that all electrical PTPs include the exact LOTO procedure number and require a secondary verification signature from the lead electrician before work begins.',
        estimatedImpact:
          'Dramatically lowers the risk of accidental energization and fatal electrocution during complex MEP phases.',
      },
    ],
  },
  {
    paramId: 'ptpEngagement',
    paramLabel: 'PTP Engagement',
    severity: 'high',
    variants: [
      {
        whyExplanation:
          "PTP completion appears high on paper, but only 41% of sampled workers could explain the control change for the day's work and two crews started before the foreman finished the huddle. Engagement is lowest on mixed-language crews and during early-morning mobilization windows.",
        correctiveAction:
          'Move PTP delivery to the workface, require foremen to verify one control back from each crew in English or Spanish, and spot-check the first three starts each day for the next two weeks.',
        estimatedImpact:
          'Should make daily planning more usable in the field and improve whether controls are actually understood before the task begins.',
      },
      {
        whyExplanation:
          'Field observations indicate that PTP huddles for the concrete subcontractor average less than 2 minutes, with workers signing the sheet without reading it. 3 workers on the deck could not name the designated fall rescue person.',
        correctiveAction:
          'Require the concrete foreman to use the "ask-back" method during huddles, ensuring at least two workers verbally confirm the day\'s primary hazard and rescue plan.',
        estimatedImpact:
          'Increases active participation and ensures critical rescue knowledge is distributed across the crew, not just held by the foreman.',
      },
      {
        whyExplanation:
          'Late arrivals are signing the PTP without receiving the briefing. In 4 separate instances this week, workers who arrived 30 minutes late bypassed the huddle and went straight to the leading edge.',
        correctiveAction:
          'Establish a strict "late-start" protocol where any worker arriving after the huddle must receive a 1-on-1 briefing from the superintendent before accessing the work zone.',
        estimatedImpact:
          'Closes a major communication gap that leaves late-arriving workers unaware of changing daily hazards and exclusion zones.',
      },
    ],
  },
  {
    paramId: 'controlsValidation',
    paramLabel: 'Controls Validation',
    severity: 'critical',
    variants: [
      {
        whyExplanation:
          'Nine of 18 planned controls reviewed during field walks were either missing, damaged, or not maintained through the shift. The largest gaps were incomplete barricades, inactive water suppression, and unverified exclusion zones around active picks.',
        correctiveAction:
          'Add a control-verification step to foreman start-up walks, require a superintendent midday check on all high-risk areas, and reopen any PTP that lists a control not present in the field.',
        estimatedImpact:
          'Should improve control reliability during execution, which is often where otherwise good planning breaks down.',
      },
      {
        whyExplanation:
          'Guardrails removed for material loading on floor 12 were not replaced for over 4 hours. The PTP stated "replace rails immediately," but field validation showed the control was ignored once production pressure increased.',
        correctiveAction:
          'Assign a dedicated safety monitor during all material loading operations with the sole responsibility of verifying edge protection is reinstated the moment the load is clear.',
        estimatedImpact:
          'Eliminates prolonged exposure to unprotected edges, addressing the most common cause of fatal falls.',
      },
      {
        whyExplanation:
          'Silica dust controls (water delivery systems) for masonry cutting were listed on the JHA but found disconnected or dry in 3 of 5 cutting stations. Workers cited "water supply issues" but continued cutting.',
        correctiveAction:
          'Implement a zero-tolerance stop-work rule for dry cutting. Provide dedicated water tanks for each station to eliminate reliance on shared site water lines.',
        estimatedImpact:
          'Ensures compliance with OSHA silica standards (1926.1153) and prevents long-term respiratory exposure.',
      },
    ],
  },
  {
    paramId: 'ppeValidation',
    paramLabel: 'PPE Validation',
    severity: 'high',
    variants: [
      {
        whyExplanation:
          'Seven PPE deviations were documented in five days, including missing chin straps, inconsistent cut-resistant glove use, and hearing protection not worn in active demo zones. Most deviations were recorded on second shift and among recently added labor crews.',
        correctiveAction:
          'Post a task-based PPE matrix at each access point, assign second-shift leads to complete start-of-shift PPE checks, and remove repeat violators from high-risk work until coaching is documented.',
        estimatedImpact:
          'Should tighten baseline protection quickly and reduce the chance that routine exposures escalate into recordable injuries.',
      },
      {
        whyExplanation:
          'Safety glasses are consistently being removed due to fogging in the high-humidity basement excavation area. 12 workers were observed without eye protection during chipping operations.',
        correctiveAction:
          'Procure and distribute high-quality anti-fog safety glasses immediately. Hold a stand-down with the excavation crew to reinforce the requirement and swap out inadequate gear.',
        estimatedImpact:
          'Directly prevents foreign-body eye injuries while addressing the root cause (equipment discomfort/unsuitability).',
      },
      {
        whyExplanation:
          'Subcontractors performing hot work were observed wearing synthetic high-visibility vests that are not flame-resistant, posing a severe burn risk from welding slag and sparks.',
        correctiveAction:
          'Audit all hot work permits and verify FR-rated clothing is worn. Confiscate non-FR vests in hot work zones and provide FR-rated alternatives at the tool crib.',
        estimatedImpact:
          'Mitigates the risk of catastrophic burn injuries by ensuring PPE matches the specific hazard of the task.',
      },
    ],
  },
  {
    paramId: 'sdsComplianceEngagement',
    paramLabel: 'SDS Compliance & Engagement',
    severity: 'medium',
    variants: [
      {
        whyExplanation:
          'Three of five active chemical products in the renovation area were not linked to the current SDS in the site system, and only 2 of 9 sampled workers could identify the required gloves or first-aid response for the product they were using. The gap is greatest on sealants, coatings, and cleaning agents introduced by specialty trades.',
        correctiveAction:
          'Refresh the digital SDS library by trade, require a foreman chemical brief before any new product is opened, and place bilingual QR-coded SDS boards at each active work zone.',
        estimatedImpact:
          'Should improve worker awareness of chemical controls and lower the likelihood of avoidable exposure, contamination, or improper response.',
      },
      {
        whyExplanation:
          'A new epoxy flooring system was introduced without an updated SDS on file. Workers were unaware of the specific ventilation requirements, leading to complaints of dizziness in enclosed spaces.',
        correctiveAction:
          'Halt epoxy application until the SDS is reviewed, temporary forced ventilation is installed, and half-mask respirators are issued if required by the manufacturer.',
        estimatedImpact:
          'Prevents acute inhalation injuries and ensures compliance with hazard communication standards.',
      },
      {
        whyExplanation:
          'Flammable solvents are being stored outside of approved flammable storage cabinets overnight. Workers are unaware of the flashpoint limits detailed in the SDS.',
        correctiveAction:
          'Conduct a site-wide sweep for improperly stored chemicals. Relocate all solvents to grounded, fire-rated cabinets and review storage requirements at the next toolbox talk.',
        estimatedImpact:
          'Significantly reduces the risk of site fires and ensures compliance with 1926.152 flammable liquids regulations.',
      },
    ],
  },
  {
    paramId: 'observationFrequency',
    paramLabel: 'Observation Frequency',
    severity: 'medium',
    variants: [
      {
        whyExplanation:
          'The project averaged 0.7 observations per 10 workers over the last two weeks, compared with a portfolio rate of 1.8 during similar phases. Coverage is especially thin on weekend work and on second shift, where activity remains high but safety visibility drops.',
        correctiveAction:
          'Set weekly observation expectations for project leaders and trade supervisors, rotate focus areas by risk, and require weekend and second-shift observation coverage starting this week.',
        estimatedImpact:
          'Should increase early hazard detection and reduce the lag between emerging field conditions and management response.',
      },
      {
        whyExplanation:
          'Superintendent observation entries have dropped by 40% since the concrete pour schedule accelerated. Safety is being viewed as the sole responsibility of the safety manager rather than a shared operational duty.',
        correctiveAction:
          'Implement a mandatory "safety walk" block on the superintendent\'s daily calendar. Require at least two documented observations per day from every field manager.',
        estimatedImpact:
          'Re-establishes operational ownership of safety and ensures hazards are caught by the people directing the work.',
      },
      {
        whyExplanation:
          'Observations are heavily clustered around the ground floor logistics areas, with almost zero observations recorded on the upper floors where the highest-risk curtain wall installation is occurring.',
        correctiveAction:
          'Assign specific floor zones to different managers for their weekly walks to force distribution of observations across the entire vertical footprint.',
        estimatedImpact:
          'Eliminates blind spots on upper floors and ensures high-risk leading edge work receives adequate oversight.',
      },
    ],
  },
  {
    paramId: 'observationSeverity',
    paramLabel: 'Observation Severity',
    severity: 'high',
    variants: [
      {
        whyExplanation:
          "Forty-three percent of this month's observations were rated high severity versus a portfolio average of 19%. The trend is concentrated in edge work, suspended-load areas, and temporary power setups rather than spread evenly across the project.",
        correctiveAction:
          'Hold a daily review of all high-severity observations with the superintendent team, assign owners before the next shift, and place senior field leaders at the three highest-risk hotspots for the next five workdays.',
        estimatedImpact:
          'Should reduce exposure to serious events by putting leadership attention on the conditions most likely to cause severe harm.',
      },
      {
        whyExplanation:
          'Over half of the observations recorded this week involved life-critical risks, specifically improper trench shoring and bypassed fall protection. This indicates a breakdown in fundamental life-saving rules.',
        correctiveAction:
          'Initiate an immediate project-wide stand-down to review life-saving rules. Implement a "three-strikes" policy for subcontractors repeatedly violating critical safety protocols.',
        estimatedImpact:
          'Sends a clear message that life-critical violations will not be tolerated, immediately reducing SIF (Serious Injury and Fatality) potential.',
      },
      {
        whyExplanation:
          'A spike in high-severity electrical observations (exposed live wires, bypassed GFCI) has occurred since the interior framing phase began, indicating poor coordination between drywall and electrical trades.',
        correctiveAction:
          'Require joint daily walks between the electrical and framing foremen to identify and secure all temporary power and exposed wiring before framing begins in a new zone.',
        estimatedImpact:
          'Reduces the risk of electrocution and fire by forcing coordination between overlapping trades.',
      },
    ],
  },
  {
    paramId: 'openObservationCount',
    paramLabel: 'Open Observation Count',
    severity: 'high',
    variants: [
      {
        whyExplanation:
          'There are 26 open observations older than seven days, including 9 older than 14 days. Most aging items involve repeated access, housekeeping, and barricade deficiencies, which means risk is staying live longer than the workfront is stable.',
        correctiveAction:
          'Create a daily burn-down list, assign one owner per item, and require field verification or documented closure within 48 hours for all observations older than one week.',
        estimatedImpact:
          'Should reduce repeated exposure and keep the observation process from becoming a backlog rather than a control tool.',
      },
      {
        whyExplanation:
          'Subcontractors are failing to close out assigned observations in the system. 18 observations related to scaffold modifications have been open for over a week, despite verbal claims that they were fixed.',
        correctiveAction:
          'Withhold progress payments for subcontractors with critical safety observations open longer than 5 days. Require photographic evidence of closure for all scaffold-related items.',
        estimatedImpact:
          'Forces accountability and ensures that identified hazards are actually mitigated and verified, not just ignored.',
      },
      {
        whyExplanation:
          'The safety team is overwhelmed and unable to verify the closure of 40+ minor housekeeping and trip hazard observations, leading to a massive backlog that obscures more critical issues.',
        correctiveAction:
          'Delegate closure verification of low-severity items to the area superintendents. Implement a "fix-it-now" policy where minor issues are corrected immediately during the walk rather than logged as open items.',
        estimatedImpact:
          'Clears the administrative backlog, allowing the safety team to focus on high-severity risks and SIF prevention.',
      },
    ],
  },
  {
    paramId: 'positiveObservationRatio',
    paramLabel: 'Positive Observation Ratio',
    severity: 'medium',
    variants: [
      {
        whyExplanation:
          'Only 12% of observations this month documented a control being used correctly, compared with a portfolio benchmark of 30%. The low ratio suggests crews are experiencing the program mostly as enforcement instead of coaching and reinforcement.',
        correctiveAction:
          'Require each foreman to submit two verified positive observations per week tied to current high-risk work, and review them during the superintendent huddle alongside negative findings.',
        estimatedImpact:
          'Should improve participation, reinforce good behaviors earlier, and make controls more repeatable from crew to crew.',
      },
      {
        whyExplanation:
          'The safety culture is becoming punitive because 95% of recorded interactions are negative findings. Workers are actively avoiding safety personnel on site.',
        correctiveAction:
          'Launch a "catch them doing it right" campaign. Empower field leaders to hand out immediate small rewards (e.g., lunch vouchers) for exemplary safety practices, and log these as positive observations.',
        estimatedImpact:
          'Shifts the site culture from compliance-driven to commitment-driven, improving worker morale and willingness to report near-misses.',
      },
      {
        whyExplanation:
          'New supervisors are only trained to look for violations, missing opportunities to highlight innovative safety solutions developed by the crews, such as custom material handling carts.',
        correctiveAction:
          'Train supervisors on how to identify and document "best practices." Share one positive observation featuring a crew\'s safety innovation at the weekly all-hands meeting.',
        estimatedImpact:
          'Encourages continuous improvement and peer-to-peer learning by highlighting successful, worker-led safety solutions.',
      },
    ],
  },
  {
    paramId: 'inspectionCompliance',
    paramLabel: 'Inspection Compliance',
    severity: 'critical',
    variants: [
      {
        whyExplanation:
          'Only 5 of 11 required inspections were completed on time this week, including misses on excavation, scaffold, and temporary power checks. Late or missed inspections are concentrated in fast-moving areas where the workfront changes daily.',
        correctiveAction:
          'Lock mandatory inspections into the superintendent calendar, assign named backups for absences, and stop work in any high-risk area that does not have the required current inspection on file.',
        estimatedImpact:
          "Should restore the project's basic assurance checks and prevent high-risk areas from operating without current review.",
      },
      {
        whyExplanation:
          'Daily crane inspections are being pencil-whipped. The logs show identical entries for 5 consecutive days, and a spot check revealed a frayed wire rope that was not documented.',
        correctiveAction:
          'Bring in a third-party inspector for an immediate comprehensive audit of all lifting equipment. Require the operator and the rigger to co-sign the daily inspection log.',
        estimatedImpact:
          'Prevents catastrophic equipment failure and ensures compliance with OSHA 1926.1412 inspection requirements.',
      },
      {
        whyExplanation:
          'Trench inspections by the competent person are occurring after crews have already entered the excavation, violating the requirement for pre-entry checks.',
        correctiveAction:
          'Implement a physical tag system at the trench entry point. The competent person must sign and date the tag each morning before any worker is authorized to enter.',
        estimatedImpact:
          'Ensures soil stability and shoring are verified before exposure, directly mitigating the risk of a fatal trench collapse.',
      },
    ],
  },
  {
    paramId: 'reinspectionRate',
    paramLabel: 'Reinspection Rate',
    severity: 'high',
    variants: [
      {
        whyExplanation:
          'Only 38% of failed items were reinspected within the target 72-hour window, versus a portfolio benchmark of 80%. Items are being marked complete in the system before field verification, especially for barricades, access corrections, and housekeeping resets.',
        correctiveAction:
          'Turn on automatic overdue alerts, block digital closure without a reinspection photo or field sign-off, and review all late reinspections at the daily superintendent meeting.',
        estimatedImpact:
          'Should reduce repeat deficiencies and increase confidence that corrective actions are truly in place.',
      },
      {
        whyExplanation:
          'Failed fall protection anchor points were marked as "repaired" but a follow-up inspection showed they were still loose. The reinspection step was entirely skipped by the responsible foreman.',
        correctiveAction:
          'Require a safety manager to physically verify and sign off on all life-critical reinspections. The foreman cannot self-certify the repair of life-safety equipment.',
        estimatedImpact:
          'Ensures that critical safety infrastructure is actually safe to use, preventing a false sense of security that could lead to a fatal fall.',
      },
      {
        whyExplanation:
          'Fire extinguisher deficiencies (missing tags, low pressure) are taking an average of 14 days to be reinspected and replaced, leaving active hot work zones vulnerable.',
        correctiveAction:
          'Establish a direct line to the fire equipment vendor for immediate swap-outs. Any hot work permit in an area with a failed extinguisher inspection is immediately revoked until replaced.',
        estimatedImpact:
          'Restores immediate fire response capability and ensures compliance with 1926.150 fire protection standards.',
      },
    ],
  },
  {
    paramId: 'workerCompetency',
    paramLabel: 'Worker Competency',
    severity: 'high',
    variants: [
      {
        whyExplanation:
          'Six workers assigned to elevated work and rigging support do not have current site-specific training or qualification records, and three recent observations involved unfamiliar equipment use. The issue is not limited to one trade and is appearing most often on newly expanded crews.',
        correctiveAction:
          'Pull unverified workers from high-risk tasks until records are confirmed, run targeted refresher training by trade this week, and require competency verification before reassignment to lifts, rigging, or energized work.',
        estimatedImpact:
          'Should reduce execution errors on high-risk tasks where unfamiliarity with site rules or equipment creates immediate exposure.',
      },
      {
        whyExplanation:
          'Operators of aerial work platforms (MEWPs) were observed driving with the platform elevated above safe travel limits. Two of the operators could not produce valid familiarization training for the specific make and model they were using.',
        correctiveAction:
          'Ground all MEWP operators without specific model familiarization. Bring in the equipment vendor to conduct on-site evaluations and issue hardhat decals for verified operators.',
        estimatedImpact:
          'Prevents tip-overs and struck-by incidents caused by unfamiliarity with equipment controls and limits.',
      },
      {
        whyExplanation:
          'A crew tasked with erecting complex system scaffolding lacked a designated competent person on site. The resulting build had missing cross-braces and improper base plate distribution.',
        correctiveAction:
          'Dismantle the non-compliant scaffold. Require the subcontractor to provide documentation of competent person training (e.g., SAIA certification) before rebuilding.',
        estimatedImpact:
          'Ensures structural integrity of temporary works and compliance with 1926.451 scaffold erection requirements.',
      },
    ],
  },
  {
    paramId: 'newWorkerRatio',
    paramLabel: 'New Worker Ratio',
    severity: 'high',
    variants: [
      {
        whyExplanation:
          'Twenty-nine percent of the current workforce has been on site less than 30 days, versus a portfolio average of 14%. Recent first-aid and observation data are skewing toward first-week workers on interior build-out and logistics tasks.',
        correctiveAction:
          'Launch a first-10-day mentoring program, pair new workers with designated lead hands, and require foreman touchpoints at the start and midpoint of each shift for all new hires.',
        estimatedImpact:
          'Should reduce first-week exposure and improve how quickly new workers learn site-specific controls and travel paths.',
      },
      {
        whyExplanation:
          "A massive influx of temporary labor for site cleanup has resulted in a 40% new worker ratio. These workers are unfamiliar with the site's exclusion zones and have been observed walking under active crane loads.",
        correctiveAction:
          'Implement a mandatory hardhat sticker system (e.g., green stickers for <30 days). Restrict temporary labor to specific, low-risk zones separated by hard barricades from active lifting operations.',
        estimatedImpact:
          'Visibly identifies vulnerable workers and physically prevents them from wandering into high-hazard areas.',
      },
      {
        whyExplanation:
          "The framing subcontractor doubled their crew size this week to meet schedule. The new hires are unfamiliar with the site's specific tie-off requirements for leading edge work, leading to multiple warnings.",
        correctiveAction:
          'Require the framing subcontractor to conduct a dedicated 2-hour site-specific fall protection orientation for all new hires before they are allowed above the ground floor.',
        estimatedImpact:
          'Closes the knowledge gap for rapid-scale crews, ensuring they understand site-specific rules that may differ from their previous jobs.',
      },
    ],
  },
  {
    paramId: 'fatigueRisk',
    paramLabel: 'Fatigue Risk',
    severity: 'high',
    variants: [
      {
        whyExplanation:
          'Three critical-path crews have worked four consecutive Saturdays, and multiple shifts exceeded 11 hours during shutdown work and hot-weather concrete operations. Two late-day first-aid cases and a spike in low-quality PTPs after 3 p.m. suggest fatigue is already affecting execution quality.',
        correctiveAction:
          'Cap high-risk work after 10 hours, rotate physically demanding tasks, add hydration and recovery triggers for hot-weather operations, and require PM approval for any extended-shift critical work.',
        estimatedImpact:
          'Should improve decision quality late in the day and reduce error-prone conditions that often precede incidents.',
      },
      {
        whyExplanation:
          'The night shift tie-in crew has been working 12-hour shifts for 14 straight days. Observations show a significant drop in PPE compliance and housekeeping standards after 2:00 AM.',
        correctiveAction:
          'Mandate a 48-hour rest period for the night shift crew. Implement a strict 6-days-on, 1-day-off policy moving forward, regardless of schedule pressures.',
        estimatedImpact:
          'Mitigates the severe cognitive and physical decline associated with chronic sleep deprivation, preventing major errors during critical tie-ins.',
      },
      {
        whyExplanation:
          'Extreme heat (Heat Index > 105°F) combined with heavy physical labor in the excavation has led to two near-miss heat exhaustion events. Workers are pushing through breaks to finish tasks.',
        correctiveAction:
          'Enforce mandatory, timed cooling breaks (15 minutes every hour) in a designated air-conditioned tent. Implement a buddy system to monitor for signs of heat stress.',
        estimatedImpact:
          'Directly prevents heat stroke and related fatigue-induced accidents, complying with OSHA National Emphasis Program on Heat.',
      },
    ],
  },
  {
    paramId: 'crewStability',
    paramLabel: 'Crew Stability',
    severity: 'medium',
    variants: [
      {
        whyExplanation:
          'The steel and decking package has seen 36% roster turnover month over month, including four foreman changes in six weeks. Frequent crew changes are disrupting workface communication and making it harder for lessons learned to stay with the team.',
        correctiveAction:
          'Freeze nonessential crew swaps on critical-path work, require handoff briefings whenever foremen change, and use the weekly OAC to flag unstable crews before they enter high-risk scopes.',
        estimatedImpact:
          'Should improve continuity of controls and reduce the reset effect that often follows rapid workforce turnover.',
      },
      {
        whyExplanation:
          'The electrical subcontractor is rotating journeymen between three different projects daily. This constant shuffling means workers are unfamiliar with the specific daily hazards and LOTO status of this site.',
        correctiveAction:
          'Require the electrical subcontractor to commit a core team to this project. Any rotating worker must undergo a full daily re-orientation before starting work.',
        estimatedImpact:
          'Ensures workers have consistent situational awareness, which is critical for complex, multi-day electrical installations.',
      },
      {
        whyExplanation:
          "High turnover in the concrete finishing crew has led to inconsistent quality and safety practices. New finishers are not familiar with the site's specific silica dust control protocols.",
        correctiveAction:
          'Pair every new finisher with a veteran "sponsor" for their first week. The sponsor is accountable for ensuring the new worker follows all site-specific environmental controls.',
        estimatedImpact:
          'Accelerates the integration of new crew members and ensures consistent application of health hazard controls despite turnover.',
      },
    ],
  },
  {
    paramId: 'supervisionRatio',
    paramLabel: 'Supervision Ratio',
    severity: 'high',
    variants: [
      {
        whyExplanation:
          'Current supervisor-to-worker ratio on the affected floors is 1:24, well above the recommended 1:12 for high-risk scopes. Several crews are operating without direct foreman presence during peak activity windows.',
        correctiveAction:
          'Add a dedicated foreman or lead hand to each high-risk workfront, and require named supervision assignments on the daily manpower board before work starts.',
        estimatedImpact:
          'Should improve real-time oversight and reduce the chance that unsafe conditions persist unnoticed during active work.',
      },
      {
        whyExplanation:
          'The drywall subcontractor has one foreman managing 40 workers spread across 5 different floors. This lack of oversight has resulted in widespread housekeeping issues and improper ladder use.',
        correctiveAction:
          'Require the subcontractor to provide one non-working foreman for every 15 workers. Halt work on floors where adequate supervision is not physically present.',
        estimatedImpact:
          'Restores basic operational control and ensures that safety standards are enforced continuously, not just when the safety manager walks by.',
      },
      {
        whyExplanation:
          'During the weekend shift, only one general superintendent was present for 80 workers from 5 different trades. A near-miss involving a crane lift occurred because no trade foreman was directing the rigging crew.',
        correctiveAction:
          'Implement a policy requiring every trade with more than 3 workers on site during weekends to have a dedicated, competent foreman present. No foreman, no work.',
        estimatedImpact:
          'Ensures that high-risk weekend work receives the same level of competent supervision and coordination as weekday operations.',
      },
    ],
  },
  // For the remaining 14 parameters, we will provide 3 variants each to ensure complete coverage.
  {
    paramId: 'contractorHistory',
    paramLabel: 'Contractors Records',
    severity: 'high',
    variants: [
      {
        whyExplanation:
          'Two mobilized subcontractors are missing updated training rosters, insurance endorsements, and recent incident history in the project system. Record gaps are delaying site verification and increasing the chance that unqualified or unbriefed workers enter active work areas.',
        correctiveAction:
          'Place both firms on conditional mobilization status, require complete records before additional manpower is approved, and review documentation completeness at every weekly contractor coordination meeting.',
        estimatedImpact:
          'Should reduce onboarding blind spots and improve confidence that each employer is meeting baseline project requirements.',
      },
      {
        whyExplanation:
          'A newly onboarded roofing contractor has an EMR of 1.4 and a history of OSHA citations for fall protection, but no site-specific mitigation plan was submitted prior to mobilization.',
        correctiveAction:
          'Require the contractor to submit a detailed, task-specific fall protection plan and mandate a full-time, non-working safety monitor from their firm while on the roof.',
        estimatedImpact:
          'Directly addresses the known historical risk profile of the contractor, preventing their past issues from becoming incidents on this site.',
      },
      {
        whyExplanation:
          "The crane supplier's annual inspection records for the tower crane expired last week, and the operator's NCCCO certification is not on file in the site office.",
        correctiveAction:
          "Immediately shut down crane operations until the annual inspection is updated and the operator's credentials are physically verified and copied for the site records.",
        estimatedImpact:
          'Prevents catastrophic equipment failure and ensures compliance with strict crane operation regulations.',
      },
    ],
  },
  {
    paramId: 'contractorObservations',
    paramLabel: 'Contractors Observations & Inspections',
    severity: 'medium',
    variants: [
      {
        whyExplanation:
          'Five of 17 active subcontractors submitted zero observations over the last 30 days despite performing high-risk work. Observation participation is concentrated among only a few trade partners, which weakens visibility across the whole jobsite.',
        correctiveAction:
          'Set minimum observation expectations by manpower, review submission rates in the OAC each week, and require lagging trade partners to complete joint field walks with project supervision.',
        estimatedImpact:
          'Should broaden field visibility and make hazard reporting less dependent on the general contractor alone.',
      },
      {
        whyExplanation:
          "The mechanical contractor's self-inspections are identical week over week, indicating they are not actually walking the site. Several severe hazards in their work area were missed by their internal checks.",
        correctiveAction:
          'Require the mechanical foreman to conduct their weekly inspection accompanied by the GC safety manager to recalibrate their hazard recognition skills.',
        estimatedImpact:
          'Improves the quality of subcontractor self-governance and ensures real hazards are identified before they cause harm.',
      },
      {
        whyExplanation:
          'Subcontractor safety reps are only logging observations for other trades, ignoring issues within their own crews to artificially inflate their safety scores.',
        correctiveAction:
          "Implement a rule that 50% of a subcontractor's required observations must be focused on their own work processes and internal continuous improvement.",
        estimatedImpact:
          'Fosters internal accountability and prevents the observation program from becoming a tool for pointing fingers at other trades.',
      },
    ],
  },
  {
    paramId: 'openIncidents',
    paramLabel: 'Open Incident Count',
    severity: 'critical',
    variants: [
      {
        whyExplanation:
          'Four recordable or medical incidents remain open, and two are past the investigation due date with no verified corrective actions in place. Open incident volume is high for the current manpower level and is consuming field leadership bandwidth without producing closure.',
        correctiveAction:
          'Assign an executive owner to each open incident, complete root-cause reviews this week, and verify corrective actions in the field before the next monthly risk review.',
        estimatedImpact:
          'Should accelerate learning from recent incidents and reduce the chance that unresolved causes stay active on the site.',
      },
      {
        whyExplanation:
          'A severe struck-by incident from last month is still marked "under investigation" because the subcontractor has not submitted their root cause analysis. The same lifting operation continues daily without updated controls.',
        correctiveAction:
          'Halt the specific lifting operation involved in the incident until the root cause analysis is submitted, reviewed, and new controls are implemented and briefed to the crew.',
        estimatedImpact:
          'Prevents a repeat of a severe incident by forcing the implementation of lessons learned before the high-risk work continues.',
      },
      {
        whyExplanation:
          'Multiple minor first-aid incidents (cuts, debris in eye) are piling up without closure. The sheer volume indicates a breakdown in basic PPE compliance and housekeeping.',
        correctiveAction:
          'Conduct a "stand-down for basics" to address PPE and housekeeping. Assign area superintendents to close out all minor incident reports within 48 hours with verified field corrections.',
        estimatedImpact:
          'Clears the administrative backlog and addresses the systemic drift in basic safety standards that leads to frequent minor injuries.',
      },
    ],
  },
  {
    paramId: 'incidentRecurrence',
    paramLabel: 'Incident Recurrence',
    severity: 'critical',
    variants: [
      {
        whyExplanation:
          'Three hand-injury events with similar root causes have occurred in six weeks across two trade partners, even though corrective actions were previously issued. The repeat pattern suggests the controls were administrative only or were not sustained in the field.',
        correctiveAction:
          'Run a recurrence review led by the project safety director, reset task-specific controls and tool selection standards, and require supervisor verification for the next 10 shifts on the affected tasks.',
        estimatedImpact:
          'Should break the repeat-incident cycle and move the response from paperwork closure to verified control change.',
      },
      {
        whyExplanation:
          'Two utility strikes have occurred in the same excavation zone within a month. The previous corrective action (updating the utility map) failed because operators were not referencing the map in the field.',
        correctiveAction:
          'Mandate physical potholing and visual verification of all utilities before mechanical excavation begins. Require a dedicated spotter whose sole job is utility avoidance.',
        estimatedImpact:
          'Eliminates reliance on administrative controls (maps) and implements engineering/physical controls to prevent catastrophic utility strikes.',
      },
      {
        whyExplanation:
          'Multiple workers have suffered sprains from slipping on the same temporary stair tower during wet weather. Adding grip tape after the first incident was insufficient as it quickly wore off.',
        correctiveAction:
          'Replace the temporary wooden stairs with a prefabricated metal stair tower featuring aggressive, self-cleaning anti-slip treads. Install proper weather protection over the stairwell.',
        estimatedImpact:
          'Permanently engineers out the slip hazard, preventing further recurring injuries in a high-traffic access point.',
      },
    ],
  },
  {
    paramId: 'nearMissRatio',
    paramLabel: 'Near-Miss Ratio',
    severity: 'high',
    variants: [
      {
        whyExplanation:
          'Eight near misses were reported this month against one recordable case, with clustering around rigging, backed vehicles, and changing travel paths. The reporting culture is positive, but the rate and concentration indicate elevated exposure in active logistics and lifting zones.',
        correctiveAction:
          'Review every near miss within 24 hours, run hotspot stand-downs at the affected areas, and track whether the same exposure reappears within the next week.',
        estimatedImpact:
          'Should convert good reporting into faster prevention and reduce the chance that the next event is more severe.',
      },
      {
        whyExplanation:
          'Zero near misses have been reported in the last 60 days despite high-risk activities and several minor first-aid cases. This indicates a suppressed reporting culture where workers fear retaliation or view reporting as pointless.',
        correctiveAction:
          'Implement an anonymous near-miss reporting system (e.g., QR codes around site). Have leadership publicly praise and act on the first few reports to demonstrate that reporting leads to positive change, not punishment.',
        estimatedImpact:
          'Uncovers hidden risks before they result in injuries by building trust and encouraging proactive hazard identification.',
      },
      {
        whyExplanation:
          'A high volume of near misses involving dropped objects from the scaffolding has been reported by the ground crews. The current toe boards are proving inadequate for the type of debris being generated.',
        correctiveAction:
          'Install full-height debris netting on all active scaffold levels. Establish a hard exclusion zone below the scaffold that is physically barricaded, not just taped off.',
        estimatedImpact:
          'Directly addresses a severe precursor to fatal struck-by incidents, protecting workers and the public below.',
      },
    ],
  },
  {
    paramId: 'correctiveActionClosure',
    paramLabel: 'Corrective Action Closure',
    severity: 'high',
    variants: [
      {
        whyExplanation:
          'Only 47% of corrective actions were closed by their due date over the last 30 days, versus an 82% portfolio benchmark. Aging actions are heaviest on shared conditions such as housekeeping, barricades, and access, where ownership is diffused across multiple trades.',
        correctiveAction:
          'Run a weekly aging-action review with the PM, superintendent, and trade foremen, assign one accountable owner per action, and escalate any item older than seven days.',
        estimatedImpact:
          'Should shorten the time that known hazards remain live and improve accountability across overlapping work scopes.',
      },
      {
        whyExplanation:
          "Corrective actions related to electrical hazards (e.g., replacing damaged extension cords) are taking an average of 5 days to close. Foremen claim they don't have immediate access to replacement equipment.",
        correctiveAction:
          'Establish a centralized, GC-managed tool crib with readily available replacement safety equipment (cords, GFCI, PPE) to eliminate procurement delays for critical safety fixes.',
        estimatedImpact:
          'Removes the logistical barrier to immediate compliance, drastically reducing the time workers are exposed to electrical hazards.',
      },
      {
        whyExplanation:
          'Subcontractors are closing corrective actions in the software without actually performing the work in the field. A spot check revealed 5 "closed" fall protection issues were still actively hazardous.',
        correctiveAction:
          'Revoke self-closure privileges for subcontractors. All high-severity corrective actions must now be physically verified and closed by a GC safety manager.',
        estimatedImpact:
          'Restores integrity to the safety management system and ensures that critical hazards are genuinely mitigated.',
      },
    ],
  },
  {
    paramId: 'projectType',
    paramLabel: 'Project Type',
    severity: 'high',
    variants: [
      {
        whyExplanation:
          'This project combines high-rise vertical work, occupied-campus logistics, utility tie-ins, and prefabricated component setting in a constrained urban footprint. Compared with a ground-up greenfield build, the mix of public interfaces, live systems, and vertical travel raises inherent baseline risk.',
        correctiveAction:
          'Use a project-type risk register, require specialty permits and owner coordination for live-area work, and schedule monthly deep dives on logistics, public protection, and utility controls.',
        estimatedImpact:
          'Should make inherent project complexity more manageable by forcing earlier coordination on the risks that are built into the job.',
      },
      {
        whyExplanation:
          'This is an active hospital renovation. The risk of infection control breaches (dust/aspergillus) and disruption to critical life-safety systems (medical gas, emergency power) presents a severe risk profile unique to healthcare construction.',
        correctiveAction:
          'Implement strict ICRA (Infection Control Risk Assessment) protocols. Require daily negative air pressure logs and mandate that all utility shutdowns have a 72-hour review and hospital facility sign-off.',
        estimatedImpact:
          'Protects vulnerable patient populations and prevents catastrophic disruptions to hospital operations.',
      },
      {
        whyExplanation:
          'The project involves deep excavation adjacent to historic, unreinforced masonry structures. The risk of undermining adjacent foundations and causing structural collapse is exceptionally high.',
        correctiveAction:
          'Install automated total stations for continuous vibration and movement monitoring of adjacent structures. Establish strict stop-work thresholds if movement exceeds 0.1 inches.',
        estimatedImpact:
          'Provides early warning of structural instability, preventing catastrophic collapse and massive liability.',
      },
    ],
  },
  {
    paramId: 'projectPhase',
    paramLabel: 'Project Phase',
    severity: 'high',
    variants: [
      {
        whyExplanation:
          'The job has moved into a multi-trade overlap phase where facade installation, MEP rough-in, interior stocking, and selective site work are all active at once. Observation density and congestion have increased sharply since the transition from structure-only work.',
        correctiveAction:
          'Re-baseline logistics plans for the overlap phase, reset zone ownership by floor and elevation, and hold phase-transition briefings with every trade before new workfronts open.',
        estimatedImpact:
          'Should reduce the coordination losses that typically show up when projects move from single-trade to multi-trade execution.',
      },
      {
        whyExplanation:
          'The project is in the final commissioning and punch-list phase. Workers are scattered across the entire building performing minor tasks, often working alone, and temporary safety systems (like guardrails) are being removed for final finishes.',
        correctiveAction:
          'Implement a strict "lone worker" protocol requiring hourly check-ins. Utilize mobile barricades for punch-list work near edges, and require daily coordination meetings to track exactly who is working where.',
        estimatedImpact:
          'Mitigates the unique risks of the finishing phase where hazards are dispersed and traditional mass-controls are no longer viable.',
      },
      {
        whyExplanation:
          'The project is entering the heavy steel erection phase during winter months. The combination of leading-edge work, heavy lifting, and icy conditions creates a peak risk profile for falls and struck-by incidents.',
        correctiveAction:
          'Mandate 100% tie-off for all steel connectors (no exceptions). Implement a daily pre-shift deck de-icing protocol and restrict crane lifts when sustained winds exceed 20 mph.',
        estimatedImpact:
          'Directly addresses the highest-fatality-risk phase of construction by layering environmental controls over strict fall protection enforcement.',
      },
    ],
  },
  {
    paramId: 'weatherSeverity',
    paramLabel: 'Weather Severity',
    severity: 'high',
    variants: [
      {
        whyExplanation:
          'The next 10-day forecast includes high heat, gusty afternoon winds, and multiple thunderstorm windows that overlap with crane use, roof work, and concrete placement. Recent first-aid cases and schedule compression increase the chance that crews push through marginal conditions.',
        correctiveAction:
          'Issue a weather-trigger matrix for heat, wind, and lightning, require pre-shift adjustments to work sequencing, and pre-stage shaded recovery areas and storm evacuation routes.',
        estimatedImpact:
          'Should reduce weather-driven decision errors and keep environmental conditions from overwhelming otherwise acceptable work plans.',
      },
      {
        whyExplanation:
          'A severe cold snap (temperatures below 10°F) is forecasted during the exterior framing phase. Workers are wearing bulky clothing that interferes with harness fit, and ice accumulation on the scaffolding is a major slip hazard.',
        correctiveAction:
          'Mandate harness fit-checks over winter gear. Apply eco-friendly de-icer to all scaffold planks daily, and implement a mandatory 15-minute warming break every 2 hours.',
        estimatedImpact:
          'Prevents cold-stress injuries, slip-and-falls on ice, and ensures fall protection equipment functions correctly over heavy clothing.',
      },
      {
        whyExplanation:
          'Heavy, sustained rainfall has saturated the site, severely compromising the stability of the main excavation and creating deep mud that is bogging down heavy equipment and creating slip hazards.',
        correctiveAction:
          'Halt all work in the excavation until a competent person re-evaluates the shoring. Bring in crushed rock to stabilize main equipment travel paths and pedestrian walkways.',
        estimatedImpact:
          'Prevents trench collapse due to soil saturation and reduces equipment accidents and worker slip-and-falls in deep mud.',
      },
    ],
  },
  {
    paramId: 'siteCongestion',
    paramLabel: 'Site Congestion',
    severity: 'critical',
    variants: [
      {
        whyExplanation:
          'Average daily manpower is up while laydown space has been reduced by facade staging and temporary utility reroutes, and 14 observations in 10 days involved blocked access or workers crossing active equipment lanes. Congestion is now affecting both production flow and emergency egress reliability.',
        correctiveAction:
          'Move large deliveries to off-hours, hard-zone laydown by trade and floor, and assign one logistics lead to manage access, pedestrian routing, and emergency-clear corridors daily.',
        estimatedImpact:
          'Should cut line-of-fire exposure and improve movement through the site during both normal operations and emergency conditions.',
      },
      {
        whyExplanation:
          'The loading dock is overwhelmed. Delivery trucks are backing up onto the active public street, and workers are manually unloading materials in the same zone where telehandlers are operating.',
        correctiveAction:
          'Implement a strict just-in-time delivery schedule using a digital booking system. Assign a dedicated flagger to control the loading dock and physically separate manual unloading from equipment operations.',
        estimatedImpact:
          'Eliminates the risk of workers being struck by backing vehicles and prevents dangerous interactions with public traffic.',
      },
      {
        whyExplanation:
          'Interior framing, electrical rough-in, and HVAC duct installation are all occurring simultaneously in the narrow corridors of floor 4. Scissor lifts are blocking egress routes and workers are climbing over materials.',
        correctiveAction:
          'Implement a "pull planning" schedule to sequence trades so they are not occupying the same corridor simultaneously. Enforce a "nothing on the floor" policy for materials in egress paths.',
        estimatedImpact:
          'Reduces struck-by and trip hazards, and ensures safe evacuation routes are maintained during dense interior build-outs.',
      },
    ],
  },
  {
    paramId: 'hazardousMaterials',
    paramLabel: 'Hazardous Materials',
    severity: 'high',
    variants: [
      {
        whyExplanation:
          'The renovation scope includes legacy coatings, suspect asbestos-containing materials, silica-generating work, and multiple active chemical products, but hazard communication is not staying current as work areas change. Three recent work packages referenced generic controls rather than product- or material-specific precautions.',
        correctiveAction:
          'Create a floor-by-floor hazardous-materials map, require JHA updates whenever material conditions change, and verify that SDS or abatement information is reviewed at the pre-task huddle.',
        estimatedImpact:
          'Should reduce the chance of uncontrolled exposure and help prevent costly contamination events or area shutdowns.',
      },
      {
        whyExplanation:
          'A subcontractor brought a highly flammable spray adhesive on site without approval. It is being used in an enclosed basement area with poor ventilation and active hot work nearby.',
        correctiveAction:
          'Immediately halt the use of the adhesive and ventilate the area. Require all chemicals to pass through a GC approval gate before being brought on site, and ban flammable adhesives in enclosed spaces.',
        estimatedImpact:
          'Prevents a catastrophic flash fire or explosion in a confined, poorly ventilated area.',
      },
      {
        whyExplanation:
          'Workers performing concrete grinding are not using the required HEPA vacuums, creating massive silica dust clouds that are exposing not only the grinders but all adjacent trades on the floor.',
        correctiveAction:
          'Stop all grinding operations. Confiscate non-compliant tools and require the subcontractor to provide shrouded grinders with integrated HEPA vacuums before work resumes.',
        estimatedImpact:
          'Ensures compliance with OSHA silica standards and protects the entire site population from hazardous respirable dust.',
      },
    ],
  },
  {
    paramId: 'heavyEquipmentDensity',
    paramLabel: 'Heavy Equipment Density',
    severity: 'high',
    variants: [
      {
        whyExplanation:
          'Fourteen pieces of rolling stock or lifting equipment were active in the constrained footprint during the last peak shift, including telehandlers, excavators, cranes, and delivery trucks. The density is outpacing the current traffic plan and increasing blind-spot and interface risk.',
        correctiveAction:
          'Limit concurrent equipment in the tightest zones, separate pedestrian and equipment routes with hard barricades, and require spotters or alternate routing for all reverse movements.',
        estimatedImpact:
          'Should reduce struck-by exposure and improve predictability of site movements during peak logistics windows.',
      },
      {
        whyExplanation:
          'Multiple scissor lifts and boom lifts are operating in close proximity during the high-bay warehouse ceiling installation. Two minor collisions between lifts have occurred this week due to overlapping swing radiuses.',
        correctiveAction:
          'Establish designated operating lanes for each lift. Require operators to honk horns before any movement and mandate a minimum 10-foot separation distance between active lifts.',
        estimatedImpact:
          'Prevents equipment collisions that could lead to tip-overs or workers being ejected from elevated platforms.',
      },
      {
        whyExplanation:
          'Excavators and articulated dump trucks are operating in the same zone where ground workers are laying pipe. The noise levels make verbal communication impossible, and spotters are frequently losing line-of-sight with operators.',
        correctiveAction:
          'Implement a strict "halo zone" policy. Ground workers must stay 20 feet away from equipment unless eye contact is made and the operator grounds the bucket and signals it is safe to approach.',
        estimatedImpact:
          'Directly mitigates the highest-risk scenario for fatal struck-by/caught-between incidents in civil construction.',
      },
    ],
  },
  {
    paramId: 'jhaQuality',
    paramLabel: 'JHA Quality',
    severity: 'high',
    variants: [
      {
        whyExplanation:
          'Only 3 of 10 JHAs reviewed this week reflected the actual sequence, trade overlap, or changed site conditions for the work being performed. Several were copied forward from prior weeks without updates for new access routes, staging limits, or adjacent operations.',
        correctiveAction:
          'Require foremen to rewrite JHAs when sequence or adjacent conditions change, add superintendent review for high-risk scopes, and reject any JHA that uses prior text without field updates.',
        estimatedImpact:
          'Should make task planning more credible and reduce the gap between written controls and real field conditions.',
      },
      {
        whyExplanation:
          'The JHA for the critical crane lift lacked specific details on rigging inspection, wind speed limits, and the exact swing path. It was a generic "crane operations" document that provided no actual guidance to the crew.',
        correctiveAction:
          'Require a task-specific critical lift plan to be submitted and approved by the project engineer and safety director 48 hours before any lift exceeding 75% of crane capacity.',
        estimatedImpact:
          'Ensures complex, high-risk operations are engineered and planned thoroughly, preventing catastrophic rigging failures.',
      },
      {
        whyExplanation:
          'JHAs for confined space entry did not list the specific atmospheric hazards expected or the exact calibration date of the gas monitor being used. The rescue plan simply stated "call 911".',
        correctiveAction:
          'Overhaul the confined space program. JHAs must detail specific gas monitor readings required, and a non-entry rescue system (tripod/winch) must be rigged and tested before entry.',
        estimatedImpact:
          'Prevents fatal atmospheric exposure and ensures a viable rescue can occur without endangering additional workers.',
      },
    ],
  },
  {
    paramId: 'toolboxEngagement',
    paramLabel: 'Toolbox Engagement',
    severity: 'medium',
    variants: [
      {
        whyExplanation:
          "Attendance is high at 94%, but post-talk spot checks show only 49% of sampled workers could state the main control from the last three toolbox talks. Engagement is lowest when topics are generic and not tied to the day's actual workfronts.",
        correctiveAction:
          "Rewrite toolbox talks around the week's top three live risks, keep them under 10 minutes, and require one worker-led readback or demonstration at each talk.",
        estimatedImpact:
          'Should improve retention and make toolbox talks more useful as a last-minute control check before work starts.',
      },
      {
        whyExplanation:
          'Toolbox talks are being delivered exclusively in English, despite 40% of the workforce being native Spanish speakers. Observations show the Spanish-speaking crews are signing the sheets without understanding the content.',
        correctiveAction:
          'Mandate that all toolbox talks be delivered bilingually. Identify bilingual lead workers to translate and ensure comprehension before signatures are collected.',
        estimatedImpact:
          'Closes a critical communication gap, ensuring all workers understand the safety expectations and hazard warnings.',
      },
      {
        whyExplanation:
          'Foremen are reading toolbox talks directly from a binder in a monotone voice in noisy environments. Workers in the back cannot hear, and no visual aids or actual equipment are used.',
        correctiveAction:
          'Train foremen on effective delivery. Require toolbox talks to be held in quieter zones and mandate the use of physical props (e.g., demonstrating how to inspect a harness rather than just reading about it).',
        estimatedImpact:
          'Transforms a compliance exercise into an effective training session, significantly improving hazard recognition.',
      },
    ],
  },
  {
    paramId: 'safetyCultureIndex',
    paramLabel: 'Safety Culture Index',
    severity: 'medium',
    variants: [
      {
        whyExplanation:
          'The latest pulse survey scored 63 versus a portfolio average of 78, with the lowest marks on stop-work confidence, supervisor consistency, and confidence that issues get fixed after being raised. The score has declined for two consecutive survey cycles despite stable incident rates.',
        correctiveAction:
          'Run targeted listening sessions by trade, have project leaders close the loop publicly on top concerns, and require supervisors to document one stop-work or intervention example each week.',
        estimatedImpact:
          'Should improve trust in the safety process, which is often necessary before reporting and intervention rates improve.',
      },
      {
        whyExplanation:
          'Workers report that schedule pressure is overriding safety. 60% of surveyed workers agreed with the statement "My foreman cares more about production than safety," leading to corners being cut on fall protection.',
        correctiveAction:
          'Realign incentives. The Project Executive must lead an all-hands meeting explicitly stating that schedule delays are acceptable if required for safety. Implement safety performance metrics into foreman bonuses.',
        estimatedImpact:
          'Removes the perceived pressure to work unsafely, directly reducing the likelihood of severe incidents caused by rushing.',
      },
      {
        whyExplanation:
          'Subcontractor workers feel alienated from the GC\'s safety program, viewing it as "rules for them, not for us." There is a clear divide in compliance between GC self-perform crews and sub-tier trades.',
        correctiveAction:
          'Create a joint worker safety committee with representatives from every major trade. Empower the committee to conduct site walks and recommend changes directly to project leadership.',
        estimatedImpact:
          'Builds an inclusive safety culture where subcontractors feel ownership of the site standards, leading to higher voluntary compliance.',
      },
    ],
  },
]

// Helper: look up explanation by paramId and optionally project name to select a variant
export function getDriverExplanation(
  paramId: string,
  projectName?: string,
): RiskDriverExplanation | undefined {
  const data = RISK_DRIVER_EXPLANATIONS_DATA.find((d) => d.paramId === paramId)
  if (!data) return undefined

  let variantIndex = 0
  if (projectName && data.variants.length > 0) {
    // Simple deterministic hash of project name to select variant
    let hash = 0
    for (let i = 0; i < projectName.length; i++) {
      hash = projectName.charCodeAt(i) + ((hash << 5) - hash)
    }
    variantIndex = Math.abs(hash) % data.variants.length
  }

  const variant = data.variants[variantIndex] || data.variants[0]

  return {
    paramId: data.paramId,
    paramLabel: data.paramLabel,
    severity: data.severity,
    whyExplanation: variant.whyExplanation,
    correctiveAction: variant.correctiveAction,
    estimatedImpact: variant.estimatedImpact,
  }
}

// Helper: look up explanation by paramId and severity
export function getDriverExplanationBySeverity(
  paramId: string,
  severity: 'critical' | 'high' | 'medium',
  projectName?: string,
): RiskDriverExplanation | undefined {
  const explanation = getDriverExplanation(paramId, projectName)
  if (!explanation) return undefined
  return { ...explanation, severity }
}
